import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Radio, RefreshCw, Unlink, Link2, Coins, Lock, BookText,
} from "lucide-react";

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm ${tone === "amber" ? "text-amber-300" : "text-slate-200"}`}>{value ?? "—"}</div>
    </div>
  );
}

function PlanChapters({ chapters }) {
  return (
    <div className="space-y-1.5" data-testid="studio-plan-chapters">
      {chapters.map((c) => (
        <div key={c.chapter_order} className="rounded-md border border-border bg-[#12161f] p-3" data-testid={`studio-chapter-${c.chapter_order}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-200"><BookText className="mr-1 inline h-3.5 w-3.5 text-amber-400" />{c.title}</span>
            <span className="font-mono text-slate-500">{c.char_count} chars · ~{c.estimated_conversion_credits} cr</span>
          </div>
          {c.applied_substitutions?.length > 0 && (
            <div className="mt-1 text-[11px] text-teal-300">{c.applied_substitutions.length} homograph substitution(s) applied to the pushed text</div>
          )}
          {c.transformed_text && <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{c.transformed_text.slice(0, 160)}…</p>}
        </div>
      ))}
    </div>
  );
}

function ConfirmDialog({ title, description, body, testid, confirmLabel, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl border-border bg-[#0d1016] text-slate-200" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-slate-100">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{description}</DialogDescription>
        </DialogHeader>
        {body}
        <Button data-testid={`${testid}-confirm`} onClick={go} disabled={busy} className="mt-2 w-full gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} {confirmLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function StudioSync() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [dialog, setDialog] = useState(null); // 'link' | 'resync'

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/studio`);
    setD(data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const doLink = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/studio/link`, { confirm: true });
      if (data?.resumable) toast.error("Sync failed partway — progress saved. Reconcile & Resume Sync to continue.");
      else toast.success("Linked & pushed to Studio.");
      setDialog(null); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const doResync = async () => {
    try { await api.post(`/projects/${projectId}/studio/resync`, { confirm: true }); toast.success("Pushed app version to Studio."); setDialog(null); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const doResume = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/studio/resume`, { confirm: true });
      toast[data?.sync_state?.status === "complete" ? "success" : "error"](data?.sync_state?.status === "complete" ? "Sync complete." : "Sync still failing — see step detail."); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const doUnlink = async () => {
    if (!window.confirm("Unlink this production from Studio? The remote Studio project is NOT deleted.")) return;
    try { const { data } = await api.post(`/projects/${projectId}/studio/unlink`, {}); toast.success(data.note); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const convertPreflight = async (order) => {
    try { const { data } = await api.post(`/projects/${projectId}/studio/convert-preflight`, { chapter_order: order }); toast.info(`~${data.estimated_conversion_credits} credits to convert “${data.title}”. ${data.message}`); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!d) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  const plan = d.plan;

  return (
    <div data-testid="studio-workspace">
      <button onClick={() => navigate(`/studio/projects/${projectId}`)} className="mb-4 flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-300" data-testid="studio-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to project
      </button>
      <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 8 · Studio Sync</div>
      <h1 className="font-serif text-4xl tracking-tight text-slate-100">ElevenLabs Studio</h1>
      <p className="mt-1 text-sm text-slate-400">Push-only mirror of the approved production. The app is the system of record — no manuscript, settings, or audio is pulled back.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Voice" value={plan.voice_name} />
        <Metric label="Model" value={plan.model} />
        <Metric label="Chapters" value={plan.chapters.length} />
        <Metric label="Dictionary" value={plan.dictionary_version || "none synced"} tone="amber" />
      </div>
      <div className="mt-2 text-xs text-slate-400"><Coins className="mr-1 inline h-3 w-3 text-amber-400" />{plan.billing_rate_label} · total est conversion ~{plan.total_estimated_conversion_credits} credits (only charged when you convert in Studio)</div>

      {!d.linked ? (
        <div className="mt-6 space-y-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="flex items-center justify-between">
              <div className="font-serif text-lg text-slate-100">Not linked to Studio</div>
              <Button data-testid="studio-link-btn" onClick={() => setDialog("link")} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"><Link2 className="h-4 w-4" /> Link & push to Studio</Button>
            </div>
            <p className="mt-1 text-xs text-slate-400">Creates ONE Studio project and pushes every chapter&apos;s exact production text (homograph substitutions applied; approved dictionary attached via locators). Idempotent — re-running never duplicates.</p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Push payload preview</div>
          <PlanChapters chapters={plan.chapters} />
        </div>
      ) : (
        <div className="mt-6 space-y-5" data-testid="studio-linked-panel">
          {d.sync_state && d.sync_state.status !== "complete" && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="studio-sync-progress">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-2 text-amber-300"><AlertTriangle className="h-4 w-4" /> Sync {d.sync_state.status} — progress saved</span>
                <Button data-testid="studio-resume-btn" onClick={doResume} className="h-8 gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"><RefreshCw className="h-4 w-4" /> Reconcile & Resume Sync</Button>
              </div>
              <ol className="space-y-1 text-xs">
                {d.sync_state.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`sync-step-${s.key}`}>
                    <span className={s.status === "done" ? "text-emerald-400" : s.status === "error" ? "text-red-400" : "text-slate-500"}>
                      {s.status === "done" ? "✓" : s.status === "error" ? "✕" : "○"}
                    </span>
                    <span className="text-slate-300">{s.label}
                      {s.status === "error" && (
                        <span className="ml-1 text-red-300">— {s.endpoint} → HTTP {s.upstream_status ?? "n/a"}: {s.detail}
                          {(s.upstream_status === 401 || s.upstream_status === 403) &&
                            <span className="block text-amber-300">Your ElevenLabs key appears to lack Studio/Projects permission. Use a key with Studio write access, then Resume.</span>}
                          {s.upstream_body && <span className="block break-all text-slate-500">{String(s.upstream_body).slice(0, 200)}</span>}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-serif text-lg text-slate-100"><Radio className="mr-1 inline h-4 w-4 text-emerald-400" /> Linked</div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-400">project {d.studio_project_id} · manuscript v{d.version_number} · last sync {d.last_sync_at ? new Date(d.last_sync_at).toLocaleString() : "—"}</div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="studio-unlink-btn" variant="outline" onClick={doUnlink} className="gap-2 border-slate-600/50"><Unlink className="h-4 w-4" /> Unlink</Button>
              </div>
            </div>
            {d.last_audit && <div className="mt-1 text-[11px] text-slate-500">last audit: {d.last_audit.ok ? "ok" : "errors"} · {d.last_audit.pushed_chapters} chapter(s){d.last_audit.errors?.length ? ` · ${d.last_audit.errors.join(", ")}` : ""}</div>}
          </div>

          {d.drift?.needs_attention && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="studio-drift-banner">
              <div className="mb-1 flex items-center gap-2 text-amber-300"><AlertTriangle className="h-4 w-4" /> Drift detected — review before overwriting</div>
              {d.drift.local?.has_local_changes && (
                <div className="text-xs text-amber-200/90" data-testid="studio-local-drift">
                  App changes not yet pushed:
                  {d.drift.local.version_changed && <span> manuscript version changed;</span>}
                  {d.drift.local.dictionary_changed && <span> dictionary changed;</span>}
                  {d.drift.local.chapters?.map((c) => <span key={c.chapter_order}> {c.title} ({c.kind});</span>)}
                </div>
              )}
              {d.drift.remote?.has_remote_changes && (
                <div className="mt-1 text-xs text-red-300" data-testid="studio-remote-drift">
                  Unexpected Studio-side changes: {d.drift.remote.chapters?.map((c) => <span key={c.chapter_order}>{c.title} ({c.kind}); </span>)}
                  Pushing the app version will overwrite these.
                </div>
              )}
              {d.drift.remote?.missing && <div className="mt-1 text-xs text-red-300">The Studio project no longer exists. Unlink and re-link to recreate it.</div>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid="studio-resync-btn" onClick={() => setDialog("resync")} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"><RefreshCw className="h-4 w-4" /> Push app version</Button>
                <Button data-testid="studio-cancel-btn" variant="outline" onClick={load} className="gap-2 border-slate-600/50">Cancel</Button>
                <Button data-testid="studio-unlink-btn-2" variant="outline" onClick={doUnlink} className="gap-2 border-slate-600/50"><Unlink className="h-4 w-4" /> Unlink</Button>
              </div>
            </div>
          )}
          {!d.drift?.needs_attention && (
            <div className="rounded-md bg-emerald-500/5 px-3 py-2 text-xs text-emerald-300" data-testid="studio-in-sync"><CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> In sync with Studio — no local or remote drift.</div>
          )}

          {/* Conversion review gate */}
          <div className="rounded-lg border border-border bg-[#12161f] p-4" data-testid="studio-conversion-gate">
            <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Lock className="h-3 w-3" /> Studio conversion (paid · held at review gate)</div>
            <div className="space-y-1.5">
              {plan.chapters.map((c) => (
                <div key={c.chapter_order} className="flex items-center justify-between rounded bg-[#0d1016] p-2 text-xs" data-testid={`studio-convert-row-${c.chapter_order}`}>
                  <span className="text-slate-300">{c.title}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-amber-300">~{c.estimated_conversion_credits} cr</span>
                    <Button size="sm" variant="outline" data-testid={`studio-convert-preflight-${c.chapter_order}`} onClick={() => convertPreflight(c.chapter_order)} className="h-7 border-amber-500/40 text-amber-200 text-xs">Estimate & convert</Button>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-slate-500">Paid conversion is intentionally not enabled in this phase — you&apos;ll see the cost estimate and an explicit gate.</div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Linked chapters</div>
          <div className="space-y-1.5">
            {d.chapters.map((c) => (
              <div key={c.chapter_order} className="flex items-center justify-between rounded-md border border-border bg-[#12161f] p-3 text-xs" data-testid={`studio-linked-chapter-${c.chapter_order}`}>
                <span className="text-slate-200"><BookText className="mr-1 inline h-3.5 w-3.5 text-amber-400" />{c.title}</span>
                <span className="font-mono text-slate-500">studio ch {c.studio_chapter_id} · {c.char_count} chars</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dialog === "link" && (
        <ConfirmDialog testid="studio-link-preflight" title="Link & push to Studio"
          description="Creates the live Studio project and pushes all chapters. Push-only; the app remains the system of record."
          confirmLabel="Confirm — create Studio project & push" onConfirm={doLink} onClose={() => setDialog(null)}
          body={<div className="space-y-2"><div className="text-xs text-slate-400">{plan.chapters.length} chapter(s) · voice {plan.voice_name} · dictionary {plan.dictionary_version || "none"}</div><PlanChapters chapters={plan.chapters} /></div>} />
      )}
      {dialog === "resync" && (
        <ConfirmDialog testid="studio-resync-preflight" title="Push app version to Studio"
          description="Overwrites drifted Studio chapters with the current app version. This is push-only and non-destructive to your local takes/exports."
          confirmLabel="Confirm — push app version" onConfirm={doResync} onClose={() => setDialog(null)}
          body={<div className="text-xs text-amber-200/90">Any unexpected Studio-side edits to changed chapters will be replaced by the app&apos;s version.</div>} />
      )}
    </div>
  );
}
